/**
 * Screen-id fingerprints for live-HTML prototypes.
 *
 * MUST stay byte-identical to @scopmx/markup-editor's internal
 * `src/utils/htmlFingerprint.ts` + `getHtmlScreenId`. The host computes the
 * `screen.hash` / `screen.from` values stored on each prototype comment, while
 * the package computes the id it compares against when deciding "have we
 * arrived at the comment's screen?". If the two implementations disagree by so
 * much as one character of input text, every stored hash is unreachable and
 * redirect reports "That comment is on another screen."
 *
 * The package does not export these helpers (only routesFromHostCommentRows,
 * htmlScreenIdFromHostRow, mergeCommentRoutesInto, fromPersistedRoutes), so the
 * algorithm is mirrored here verbatim. Re-verify against the package on every
 * markup-editor upgrade — drift is silent, and only shows up on a device that
 * has no cached IndexedDB drawing to fall back on.
 */

const CHROME_SELECTOR =
    "header, nav, footer, script, style, noscript, [role='navigation'], [role='banner'], [role='contentinfo']"
const UI_SELECTOR =
    "h1, h2, h3, h4, button, a, label, [role='button'], [role='heading'], input, img"
/** Live regions mutate on their own; their text is never part of screen identity. */
const LIVE_REGION_SELECTOR =
    "[role='status'], [role='alert'], [role='log'], [aria-live='polite'], [aria-live='assertive'], output"

const MAX_OVERLAY_DEPTH = 3
const MAX_OVERLAY_NODES = 200
const OVERLAY_BAR_HEIGHT = 96
const OVERLAY_MAX_WIDTH = 480
const OVERLAY_MAX_HEIGHT = 320

/** FNV-1a, base36 — the package's `shortHash`. */
export const shortHtmlHash = (value) => {
    let hash = 2166136261
    const text = String(value || '')
    for (let i = 0; i < text.length; i += 1) {
        hash ^= text.charCodeAt(i)
        hash = Math.imul(hash, 16777619)
    }
    return (hash >>> 0).toString(36)
}

export const isTransientHtmlFingerprint = (fingerprint) => {
    const text = String(fingerprint || '')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase()
    if (text.length < 12) return true
    return (
        text.indexOf('unpacking') !== -1 ||
        text.indexOf('this page requires javascript') !== -1 ||
        text === 'loading...' ||
        text === 'loading'
    )
}

/** Package `u` — page chrome, excluded from the UI list. */
const isChromeElement = (el, win) => {
    if (el.closest && el.closest(CHROME_SELECTOR)) return true
    const style = win.getComputedStyle(el)
    if (style.position !== 'fixed' && style.position !== 'sticky') return false
    const box = el.getBoundingClientRect()
    // NOTE: the 280px floor matters — a plain innerHeight*0.35 misclassifies
    // banners in short iframes and mints a different hash than the package.
    const limit = Math.max(280, win.innerHeight * 0.35)
    return box.top <= 8 && box.height > 0 && box.height < limit
}

/** Package `d` — is this element actually rendered? */
const isVisibleElement = (el, win) => {
    const style = win.getComputedStyle(el)
    if (
        style.display === 'none' ||
        style.visibility === 'hidden' ||
        parseFloat(style.opacity || '1') < 0.05
    ) {
        return false
    }
    const HTMLElementCtor = win.HTMLElement
    if (
        !HTMLElementCtor ||
        !(el instanceof HTMLElementCtor) ||
        (el.offsetParent === null && style.position !== 'fixed')
    ) {
        return false
    }
    const box = el.getBoundingClientRect()
    return !(box.width < 1 || box.height < 1)
}

/** Package `f` — a floating overlay (toast, tooltip, sticky bar, modal). */
const isOverlayElement = (el, win) => {
    if (win.getComputedStyle(el).position !== 'fixed') return false
    const box = el.getBoundingClientRect()
    if (box.width < 1 && box.height < 1) return true
    if (box.height > 0 && box.height <= OVERLAY_BAR_HEIGHT) return true
    return box.width <= OVERLAY_MAX_WIDTH && box.height <= OVERLAY_MAX_HEIGHT
}

/** Package `p` — live regions plus a bounded BFS for floating overlays. */
const collectExcludedElements = (body, win) => {
    const excluded = []
    const liveRegions = body.querySelectorAll(LIVE_REGION_SELECTOR)
    for (let i = 0; i < liveRegions.length; i += 1) {
        excluded.push(liveRegions[i])
    }
    const queue = []
    for (let i = 0; i < body.children.length; i += 1) {
        queue.push({ el: body.children[i], depth: 1 })
    }
    let visited = 0
    while (queue.length > 0 && visited < MAX_OVERLAY_NODES) {
        const node = queue.shift()
        visited += 1
        if (isOverlayElement(node.el, win)) {
            excluded.push(node.el)
            continue
        }
        if (node.depth >= MAX_OVERLAY_DEPTH) continue
        for (let i = 0; i < node.el.children.length; i += 1) {
            queue.push({ el: node.el.children[i], depth: node.depth + 1 })
        }
    }
    return excluded
}

/** Package `m` — is `el` one of, or inside, an excluded element? */
const isWithinExcluded = (excluded, el) => {
    for (let i = 0; i < excluded.length; i += 1) {
        if (excluded[i] === el || excluded[i].contains(el)) return true
    }
    return false
}

/** Package `h` — does `el` contain any excluded element? */
const containsExcluded = (excluded, el) => {
    for (let i = 0; i < excluded.length; i += 1) {
        if (el.contains(excluded[i])) return true
    }
    return false
}

/**
 * Package `g` — body text with excluded subtrees removed.
 * Plain `body.innerText` is NOT equivalent: any toast or live region on the
 * page shifts `len` / `head` / `mid` / `tail` and changes the hash.
 */
const textExcluding = (body, excluded) => {
    if (excluded.length === 0) return body.innerText || ''
    const parts = []
    const walk = (node) => {
        for (let i = 0; i < node.children.length; i += 1) {
            const child = node.children[i]
            if (isWithinExcluded(excluded, child)) continue
            if (containsExcluded(excluded, child)) {
                walk(child)
                continue
            }
            if (child.getClientRects().length !== 0) {
                parts.push(child.innerText || '')
            }
        }
    }
    walk(body)
    return parts.join(' ')
}

/** Package `_` — fingerprintDocument. */
export const fingerprintHtmlDocument = (doc) => {
    const body = doc?.body
    const win = doc?.defaultView
    if (!body || !win) return ''
    const excluded = collectExcludedElements(body, win)
    const bodyText = textExcluding(body, excluded)
        .replace(/\s+/g, ' ')
        .trim()
    const midStart = Math.max(0, Math.floor(bodyText.length / 2) - 100)
    const ui = Array.prototype.slice
        .call(body.querySelectorAll(UI_SELECTOR))
        .filter(
            (el) =>
                isVisibleElement(el, win) &&
                !isChromeElement(el, win) &&
                !isWithinExcluded(excluded, el)
        )
        .map((el) => {
            const tag = el.tagName.toLowerCase()
            if (tag === 'img') {
                return `img:${(el.getAttribute('alt') || '').slice(0, 40)}`
            }
            if (tag === 'input') {
                return `input:${
                    el.getAttribute('name') ||
                    el.getAttribute('placeholder') ||
                    el.getAttribute('type') ||
                    ''
                }`
            }
            return `${tag}:${(el.textContent || '')
                .replace(/\s+/g, ' ')
                .trim()
                .slice(0, 40)}`
        })
        .filter((item) => item.slice(-1) !== ':')
        .slice(0, 60)
        .join('>')
    return [
        `title:${(doc.title || '').trim()}`,
        `len:${bodyText.length}`,
        `head:${bodyText.slice(0, 280)}`,
        `mid:${bodyText.slice(midStart, midStart + 200)}`,
        `tail:${bodyText.slice(-200)}`,
        `ui:${ui}`,
    ].join('||')
}

/** Package `v` — screenIdForDocument. */
export const screenIdForHtmlDocument = (doc, win) => {
    const fingerprint = fingerprintHtmlDocument(doc)
    if (isTransientHtmlFingerprint(fingerprint)) return 'html:loading'
    const loc = win.location
    const locPart =
        loc.protocol === 'blob:' ||
        loc.protocol === 'about:' ||
        loc.protocol === 'file:'
            ? loc.hash || ''
            : loc.pathname + loc.search + loc.hash
    return `html:doc:${shortHtmlHash(`${locPart}||${fingerprint}`)}`
}

const UUID_IN_HASH = /#([0-9a-f-]{36})\b/i

/**
 * Package `oi` — id for a nested frame.
 * The `about:blank#<uuid>` forms matter for exported/bundled prototypes that
 * page through frames rather than re-rendering one document.
 */
const nestedFrameScreenId = (frame) => {
    try {
        const doc = frame.contentDocument
        if (doc?.body) {
            const fingerprint = fingerprintHtmlDocument(doc)
            if (!isTransientHtmlFingerprint(fingerprint)) {
                return `frame:${shortHtmlHash(fingerprint)}`
            }
        }
    } catch {
        // cross-origin nested frame
    }
    const fromAttr = String(frame.getAttribute('src') || '').match(
        /^about:blank#([0-9a-f-]{36})/i
    )
    if (fromAttr) return `page:${fromAttr[1]}`
    const fromSrc = String(frame.src || '').match(UUID_IN_HASH)
    if (fromSrc) return `page:${fromSrc[1]}`
    try {
        const fromHash = String(
            frame.contentWindow?.location?.hash || ''
        ).match(UUID_IN_HASH)
        if (fromHash) return `page:${fromHash[1]}`
    } catch {
        // cross-origin nested frame
    }
    return null
}

/** Package `si` — getHtmlScreenId. */
export const screenIdForIframe = (iframe) => {
    if (!iframe) return 'html:none'
    const doc = iframe.contentDocument
    const win = iframe.contentWindow
    if (!doc || !win) return 'html:loading'
    const nested = Array.from(doc.querySelectorAll('iframe'))
    if (nested.length > 0) {
        for (const frame of nested) {
            const id = nestedFrameScreenId(frame)
            if (id) return `html:${id}`
        }
        return 'html:loading'
    }
    return screenIdForHtmlDocument(doc, win)
}
